<template>
  <div>
    <div class="flex flex-col">
      <div class="flex flex-row items-center">
        <p class="mr-1 font-semibold text-3xl">
          {{ $route.meta.title }}
        </p>
        <p class="ml-1 px-3 py-2 bg-primary rounded-lg font-semibold text-white">
          {{ startCase(`${charterType} CHARTER`) }}
        </p>
        <vs-button
          v-show="handleTerminateLogButtonVisibility()"
          class="w-32 ml-auto font-semibold"
          :color="isContractTypeEdit() ? 'danger' : 'secondary'"
          @click="handleTerminateLogButtonOnClick"
        >
          {{ isContractTypeEdit() ? 'Terminate' : 'Log' }}
        </vs-button>
      </div>
      <div class="card-container">
        <vs-row
          vs-w="12"
          class="mt-3 px-3"
        >
          <vs-col
            vs-w="6"
            v-for="(item, index) of generalForm"
            :key="item.key"
            :class="[((index + 1) % 2) ? 'pr-2' : 'pl-2', {'mt-5': ((index + 1) > 2)}]"
          >
            <p class="mb-3 font-semibold">
              {{ item.title }}
            </p>
            <TextArea
              v-if="item.key === 'remark'"
              v-model="item.value"
              :danger="item.isValid === false"
              :danger-text="'* Required'"
              :disabled="item.isDisabled"
              :placeholder="item.placeholder"
            />
            <select-with-currency
              v-else-if="item.key === 'rent_price'"
              v-model="item.value"
              :is-valid="item.isValid"
              :placeholder="item.placeholder"
              :options="currencyOptions"
              :rate-option="currencyRates"
              :disabled="item.isDisabled"
            />
            <select-with-search
              hide-search
              is-object-model
              v-else-if="(!item.isFreeInput && !item.isDate) && !item.child"
              v-model="item.value"
              :is-valid="item.isValid"
              :placeholder="item.placeholder"
              :disabled="item.isDisabled"
              :options="generateOptionsByKey(item.key)"
            />
            <date-picker
              v-else-if="(!item.isFreeInput && item.isDate) && !item.child"
              v-model="item.value"
              type="date"
              format="DD-MMM-YYYY"
              class="min-w-full"
              :is-valid="item.isValid"
              :placeholder="item.placeholder"
              :disabled="item.isDisabled"
              :disabled-date="(date) => disableDate(item, date)"
            />
            <vs-input
              v-else-if="(item.isFreeInput && !item.isDate) && !item.child"
              v-model="item.value"
              class="min-w-full"
              :danger="item.isValid === false"
              :danger-text="'* Required'"
              :placeholder="item.placeholder"
              :disabled="item.isDisabled"
            />
          </vs-col>
        </vs-row>
      </div>
      <multiple-attachment
        v-model="attachments"
        :is-uploading="isUploading"
        :disabled="isContractTypeDetail()"
        @upload="uploadAttachment"
        @delete="deleteAttachment"
      />
      <div
        v-if="charterType === 'TIME'"
        class="mt-5 flex flex-row"
      >
        <div
          v-if="bargeData.length"
          class="mr-3 w-1/2 card-table-container"
        >
          <div class="flex flex-row justify-between">
            <div class="flex flex-col justify-center">
              <span class="font-semibold">Barge List</span>
              <vs-progress
                indeterminate
                v-show="isSwitchingUnitStatus"
                color="secondary"
                class="mt-5"
              />
            </div>
            <vs-button
              v-show="!isContractTypeDetail()"
              type="filled"
              color="secondary"
              class="w-32 font-semibold"
              @click="showModalByBoatType('barge')"
            >
              Add
            </vs-button>
          </div>
          <data-table
            hover-flat
            ref="bargeTable"
            class="data-table-bg-primary"
            body-height="100vh"
            :table-header="bargeHeaders"
            :table-data="bargeData"
          >
            <template #action="{ row, index }">
              <div class="flex flex-row">
                <div
                  v-show="!isContractTypeDetail()"
                  class="my-1 px-1 mr-2 action-button-secondary"
                >
                  <vs-icon
                    icon="more_horiz"
                    color="secondary"
                    size="small"
                    @click="row.isMoreShown = !row.isMoreShown"
                  />
                  <div
                    v-if="row.isMoreShown"
                    class="more-container"
                  >
                    <ul>
                      <li
                        class="text-danger"
                        @click="handleDeleteOnMoreMenu('barge', index)"
                      >
                        Delete
                      </li>
                    </ul>
                  </div>
                </div>
                <vs-button
                  type="border"
                  color="secondary"
                  class="font-semibold text-secondary"
                  @click="goToBargeCertificate(row)"
                >
                  Certificate
                </vs-button>
              </div>
            </template>

            <template #doc_status="{ row }">
              <span
                :class="[row.doc_status.toLowerCase() === 'complete'
                  ? 'doc-status-bg-success'
                  : 'doc-status-bg-danger']"
              >{{ startCase(row.doc_status) }}</span>
            </template>

            <template #unit_status="{ row, index }">
              <vs-switch
                v-model="bargeUnitStatuses[index]"
                color="secondary"
                :disabled="isContractTypeDetail() || isSwitchingUnitStatus"
                @change="putUnitStatus('barge', { id: row.id, active: bargeUnitStatuses[index] })"
              />
            </template>
          </data-table>
        </div>
        <no-data
          v-else
          header
          title="No Barge to shown"
          message="Please click button to add data"
          class="mr-3 w-1/2"
        >
          <template #header>
            <div class="flex flex-row justify-end">
              <vs-button
                v-show="!isContractTypeDetail()"
                type="filled"
                color="secondary"
                class="w-32 font-semibold"
                @click="showModalByBoatType('barge')"
              >
                Add
              </vs-button>
            </div>
          </template>
        </no-data>
        <div
          v-if="tugData.length"
          class="ml-3 w-1/2 card-table-container"
        >
          <div class="flex flex-row justify-between">
            <div class="flex flex-col justify-center">
              <span class="font-semibold">Tug List</span>
              <vs-progress
                indeterminate
                v-show="isSwitchingUnitStatus"
                color="secondary"
                class="mt-5"
              />
            </div>
            <vs-button
              v-show="!isContractTypeDetail()"
              type="filled"
              color="secondary"
              class="w-32 font-semibold"
              @click="showModalByBoatType('tug')"
            >
              Add
            </vs-button>
          </div>
          <data-table
            hover-flat
            ref="tugTable"
            class="data-table-bg-primary"
            body-height="100vh"
            :table-header="tugHeaders"
            :table-data="tugData"
          >
            <template #action="{ row, index }">
              <div class="flex flex-row">
                <div
                  v-show="!isContractTypeDetail()"
                  class="my-1 px-1 mr-2 action-button-secondary"
                >
                  <vs-icon
                    icon="more_horiz"
                    color="secondary"
                    size="small"
                    @click="row.isMoreShown = !row.isMoreShown"
                  />
                  <div
                    v-if="row.isMoreShown"
                    class="more-container"
                  >
                    <ul>
                      <li
                        class="text-danger"
                        @click="handleDeleteOnMoreMenu('tug', index)"
                      >
                        Delete
                      </li>
                    </ul>
                  </div>
                </div>
                <vs-button
                  type="border"
                  color="secondary"
                  class="font-semibold text-secondary"
                  @click="goToTugCertificate(row)"
                >
                  Certificate
                </vs-button>
              </div>
            </template>

            <template #doc_status="{ row }">
              <span
                :class="[row.doc_status.toLowerCase() === 'complete'
                  ? 'doc-status-bg-success'
                  : 'doc-status-bg-danger']"
              >{{ startCase(row.doc_status) }}</span>
            </template>

            <template #unit_status="{ row, index }">
              <vs-switch
                v-model="tugUnitStatuses[index]"
                color="secondary"
                :disabled="isContractTypeDetail() || isSwitchingUnitStatus"
                @change="putUnitStatus('tug', { id: row.id, active: tugUnitStatuses[index] })"
              />
            </template>
          </data-table>
        </div>
        <no-data
          v-else
          header
          title="No Tug to shown"
          message="Please click button to add data"
          class="ml-3 w-1/2"
        >
          <template #header>
            <div class="flex flex-row justify-end">
              <vs-button
                v-show="!isContractTypeDetail()"
                type="filled"
                color="secondary"
                class="w-32 font-semibold"
                @click="showModalByBoatType('tug')"
              >
                Add
              </vs-button>
            </div>
          </template>
        </no-data>
      </div>
      <base-custom-modal :is-shown.sync="isModalShown">
        <template #title>
          <span class="font-semibold text-lg">{{ modal.header }}</span>
        </template>

        <template #body>
          <div class="flex flex-col">
            <span class="mb-3 font-semibold">{{ modal.title }}</span>
            <select-with-search
              is-object-model
              v-model="modal.value"
              class="select-with-search-bg-white"
              :options="generateBargeOrTugOptions(modal.type)"
            />
          </div>
        </template>

        <template #footer>
          <div class="my-5 flex flex-row justify-end">
            <vs-button
              type="border"
              color="secondary"
              class="w-32 mr-2 font-semibold"
              @click="closeModal"
            >
              Cancel
            </vs-button>
            <vs-button
              type="filled"
              color="secondary"
              class="w-32 ml-2 font-semibold"
              @click="upsertBoatByType"
            >
              Ok
            </vs-button>
          </div>
        </template>
      </base-custom-modal>
      <base-custom-modal :is-shown.sync="isEditReasonModalShown">
        <template #title>
          <span class="font-semibold text-lg">Reason</span>
        </template>

        <template #body>
          <div class="flex flex-col">
            <span class="font-semibold">Reason</span>
            <vs-textarea
              v-model="reason"
              placeholder="Enter reason here"
              class="reason-textarea"
            />
          </div>
        </template>

        <template #footer>
          <div class="flex flex-row justify-end">
            <vs-button
              type="border"
              color="secondary"
              class="w-32 mr-2 font-semibold"
              @click="isEditReasonModalShown = false"
            >
              Cancel
            </vs-button>
            <vs-button
              ref="editButton"
              type="filled"
              color="secondary"
              class="w-32 ml-2 font-semibold vs-con-loading__container"
              @click="putContract()"
            >
              Submit
            </vs-button>
          </div>
        </template>
      </base-custom-modal>
      <div
        v-show="!isContractTypeDetail()"
        class="mt-5 flex flex-row justify-end"
      >
        <vs-button
          type="border"
          color="secondary"
          class="w-32 mr-2 font-semibold"
          :disabled="isProcessing"
          @click="cancelForm"
        >
          Cancel
        </vs-button>
        <vs-button
          ref="submitButton"
          type="filled"
          color="secondary"
          class="w-32 ml-2 font-semibold vs-con-loading__container"
          @click="isContractTypeEdit() ? isEditReasonModalShown = true : postContract()"
        >
          {{ isContractTypeEdit() ? 'Save' : 'Submit' }}
        </vs-button>
      </div>
    </div>
    <terminate-pop-up
      :active.sync="isPopUpShown"
      @terminate="deleteContract"
      @close="isPopUpShown = false"
    />
    <log-sidecar
      :is-shown.sync="isLogShown"
      :barge-log-data="contractLogItems"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import BaseCustomModal from '@/components/baseCustomModal'
import DataTable from '@/components/dataTable'
import DatePicker from '@/components/datePicker'
import LogSidecar from '@/components/logSidecar'
import MultipleAttachment from '@/components/multipleAttachment'
import NoData from '@/components/noData'
import SelectWithCurrency from '@/components/selectWithCurrency'
import SelectWithSearch from '@/components/selectWithSearch'
import TerminatePopUp from '@/components/terminatePopUp'
import TextArea from '@/components/textArea'
import {MAX_FILE_UPLOAD_SIZE_BYTES, PAGE_TITLE} from '@/fixtures/Constants'
import ContractSpotForm from '@/fixtures/contract-spot-charter-form.json'
import ContractTimeForm from '@/fixtures/contract-time-charter-form.json'

export default {
  components: {
    BaseCustomModal,
    DataTable,
    DatePicker,
    LogSidecar,
    MultipleAttachment,
    NoData,
    SelectWithCurrency,
    SelectWithSearch,
    TerminatePopUp,
    TextArea
  },
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  data() {
    return {
      ContractSpotForm,
      ContractTimeForm,
      isModalShown: false,
      isEditReasonModalShown: false,
      isEditBargeTug: false,
      isProcessing: false,
      isUploading: false,
      isDetailLoading: false,
      isFromCertificate: false,
      isPopUpShown: false,
      isLogShown: false,
      isSwitchingUnitStatus: false,
      editIndex: null,
      deleteAttachmentIndex: null,
      modal: {
        type: null,
        header: null,
        title: null,
        value: null
      },
      bargeHeaders: [
        { key: 'action', label: 'Action' },
        { key: 'barge_name', label: 'Barge Name' },
        { key: 'doc_status', label: 'Doc Status' },
        { key: 'unit_status', label: 'Unit Status' }
      ],
      tugHeaders: [
        { key: 'action', label: 'Action' },
        { key: 'tug_name', label: 'Tug Name' },
        { key: 'doc_status', label: 'Doc Status' },
        { key: 'unit_status', label: 'Unit Status' }
      ],
      underCompanyOptions: [],
      bargeSizeOptions: [],
      contractorOptions: [],
      routeOptions: [],
      currencyOptions: [{ value: 'idr', text: 'IDR' }],
      currencyRates: [{ value: 'idr', rate: 1 }],
      bargeOptions: [],
      tugOptions: [],
      bargeUnitStatuses: ContractTimeForm.bargeUnitStatuses,
      tugUnitStatuses: ContractTimeForm.tugUnitStatuses,
      reason: '',
      contractLogItems: []
    }
  },
  computed: {
    ...mapGetters({
      charterType: 'Contract/charterType',
      underCompanies: 'Contract/underCompanies',
      bargeSizes: 'Contract/bargeSizes',
      routes: 'Contract/routes',
      contractors: 'Contract/contractors',
      currencies: 'Contract/currencies',
      barges: 'Contract/barges',
      tugs: 'Contract/tugs',
      attachment: 'Contract/attachment',
      isDeleteAttachmentSuccess: 'Contract/isDeleteAttachmentSuccess',
      contractDetail: 'Contract/contractDetail',
      contractLog: 'Contract/contractLog'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    generalForm() {
      return this.charterType === 'TIME'
        ? ContractTimeForm.data
        : ContractSpotForm.data
    },
    attachments() {
      return this.charterType === 'TIME'
        ? ContractTimeForm.attachments
        : ContractSpotForm.attachments
    },
    bargeData() {
      return ContractTimeForm.barges
    },
    tugData() {
      return ContractTimeForm.tugs
    },
    bargeUnitStatusesData() {
      return ContractTimeForm.bargeUnitStatuses
    },
    tugUnitStatusesData() {
      return ContractTimeForm.tugUnitStatuses
    },
    contractor() {
      return this.charterType === 'TIME'
        ? ContractTimeForm.data[3].value
        : ContractSpotForm.data[3].value
    },
    contractStart() {
      return this.charterType === 'TIME'
        ? ContractTimeForm.data[4].value
        : ContractSpotForm.data[4].value
    },
    contractEnd() {
      return this.charterType === 'TIME'
        ? ContractTimeForm.data[5].value
        : ContractSpotForm.data[5].value
    }
  },
  watch: {
    isDetailLoading: {
      handler(val) {
        if (val) {
          this.showPageLoading(this)
          return
        }

        this.closePageLoading(this)
      }
    },
    underCompanies: {
      handler(val) {
        this.underCompanyOptions = val.map(item => ({ value: item.id, text: item.code }))
      }
    },
    bargeSizes: {
      handler(val) {
        this.bargeSizeOptions = val.map(item => ({ value: item.id, text: item.name }))
      }
    },
    routes: {
      handler(val) {
        this.routeOptions = val.map(item => ({ value: item.id, text: item.name }))
      }
    },
    contractors: {
      handler(val) {
        this.contractorOptions = val.map(item => ({ value: item.id, text: item.name }))
      }
    },
    currencies: {
      handler(val) {
        this.currencyOptions = val.map(item => ({
          value: this.lowerCase(item.currency_code),
          text: item.currency_code
        }))

        this.currencyRates = val.map(item => ({
          value: this.lowerCase(item.currency_code),
          rate: item.exchange_rate_to_idr
        }))
      }
    },
    barges: {
      handler(val) {
        this.bargeOptions = val.map(item => ({ value: item, text: item.barge_name }))
      }
    },
    tugs: {
      handler(val) {
        this.tugOptions = val.map(item => ({ value: item, text: item.tug_name }))
      }
    },
    bargeUnitStatusesData: {
      handler(val) {
        this.bargeUnitStatuses = val
      }
    },
    tugUnitStatusesData: {
      handler(val) {
        this.tugUnitStatuses = val
      }
    },
    attachment: {
      handler(val) {
        if (val) {
          this.attachments.push(val)
        }
      }
    },
    isDeleteAttachmentSuccess: {
      handler(val) {
        if (val) {
          this.attachments.splice(this.deleteAttachmentIndex, 1)
        }

        this.deleteAttachmentIndex = null
      }
    },
    contractor: {
      handler(val) {
        if (val) {
          this.fetchTugsAndBarges(val, 'contractor')
        }
      }
    },
    contractDetail: {
      handler(val) {
        if (val) {
          this.parseDetailContract(val)
        }
      }
    },
    contractStart: {
      handler(val) {
        if (this.isContractTypeEdit() && this.moment(val).isSameOrAfter(this.contractEnd)) {
          if (this.charterType === 'TIME') {
            ContractTimeForm.data[5].value = this.moment(val).add(1, 'days').toDate()
            return
          }

          ContractSpotForm.data[5].value = this.moment(val).add(1, 'days').toDate()
        }
      }
    },
    contractLog: {
      handler(val) {
        if (!this.isEmpty(val)) {
          this.contractLogItems = val
        }
      }
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.isFromCertificate = from.path.includes('create') && from.path.includes('certificate')
      next()
    })
  },
  mounted() {
    if (this.isContractTypeDetail() || this.isContractTypeEdit()) {
      this.fetchOptionAndDetailData()
      return
    }

    this.setLeaseBase()
    this.fetchUnderCompanies()
    this.fetchBargeSizes()
    this.fetchRoutes()
    this.fetchContractors()
    this.fetchCurrencies()

    this.parseContractFormWhenBackFromCertificate()
  },
  destroyed() {
    this.resetForm()
  },
  methods: {
    async fetchContractDetail() {
      await this.$store.dispatch('Contract/fetchContractDetail', this.$route.params.id)
    },
    async getVesselLineUpLog() {
      await this.$store.dispatch('Contract/fetchContractLog', { contract_id: this.contractDetail.id })
    },
    async fetchUnderCompanies() {
      await this.$store.dispatch('Contract/fetchMasters', 'under_company')
    },
    async fetchBargeSizes() {
      await this.$store.dispatch('Contract/fetchMasters', 'barge_size')
    },
    async fetchRoutes() {
      await this.$store.dispatch('Contract/fetchMasters', 'route')
    },
    async fetchContractors() {
      await this.$store.dispatch('Contract/fetchMasters', 'contractor')
    },
    async fetchCurrencies() {
      await this.$store.dispatch('Contract/fetchMasters', 'currency')
    },
    async fetchBarges(id) {
      await this.$store.dispatch('Contract/fetchBarges', { contractor_id: id })
    },
    async fetchTugs(id) {
      await this.$store.dispatch('Contract/fetchTugs', { contractor_id: id })
    },
    async fetchTugsAndBarges(val, key) {
      if (key === 'contractor') {
        await this.fetchTugs(val.value)
        await this.fetchBarges(val.value)
      }
    },
    async uploadAttachment(file) {
      if (file.size > MAX_FILE_UPLOAD_SIZE_BYTES) {
        this.showFailedNotification(this, 'File may not be greater than 5MB')
        return
      }

      this.isUploading = true
      await this.$store.dispatch('Contract/uploadAttachment', file)

      this.isUploading = false
    },
    async deleteAttachment(item) {
      this.isUploading = true
      this.deleteAttachmentIndex = item.index
      await this.$store.dispatch('Contract/deleteAttachment', item.file.dms_node_id)

      this.isUploading = false
    },
    async postContract() {
      if (this.validate()) {
        this.showFailedNotification(this, 'Some field is empty, please fill it first')
        return
      }

      if (!this.attachments.length) {
        this.showFailedNotification(this, 'Attachment should have minimum one file')
        return
      }

      this.isProcessing = true
      this.changeButtonState(this.$refs.submitButton.$el)
      const form = this.charterType === 'TIME'
        ? this.transformDataForPost(ContractTimeForm.data, ContractTimeForm.attachments, ContractTimeForm.barges, ContractTimeForm.tugs)
        : this.transformDataForPost(ContractSpotForm.data, ContractSpotForm.attachments, [], [])

      try {
        await this.$store.dispatch('Contract/postContract', form)
        this.showSuccessNotification(this, 'Contract successfully sent')
        setTimeout(() => {
          this.$router.push({ name: 'ContractManagementList' })
        }, 2000)
      } catch (err) {
        this.isProcessing = false
        this.changeButtonState(this.$refs.submitButton.$el)
        this.showFailedNotification(this, 'Cannot send contract, please try again')
      }
    },
    async putContract() {
      if (!this.attachments.length) {
        this.showFailedNotification(this, 'Attachment should have minimum one file')
        return
      }

      this.isProcessing = true
      this.changeButtonState(this.$refs.submitButton.$el)
      const form = this.charterType === 'TIME'
        ? this.generateFormForEdit(ContractTimeForm.data, ContractTimeForm.attachments, ContractTimeForm.barges, ContractTimeForm.tugs)
        : this.generateFormForEdit(ContractSpotForm.data, ContractSpotForm.attachments, [], [])

      if (form.contract_end &&
        this.moment(form.contract_end, 'YYYY-MM-DD').isSameOrBefore(this.contractDetail.contract_end)) {
        this.showWarningNotification(this, 'Contract start or end date cannot be the same with the previous one')
        return
      }

      try {
        await this.$store.dispatch('Contract/putContract', {
          ...form,
          type: this.charterType,
          reason: this.reason
        })
        this.showSuccessNotification(this, 'Contract successfully updated')
        setTimeout(() => {
          this.$router.push({ name: 'ContractManagementList' })
        }, 2000)
      } catch (err) {
        this.isProcessing = false
        this.changeButtonState(this.$refs.submitButton.$el)
        this.showFailedNotification(this, 'Cannot update contract, please try again')
      }
    },
    async deleteContract() {
      try {
        this.isPopUpShown = false
        this.isDetailLoading = true
        await this.$store.dispatch('Contract/deleteContract', this.contractDetail.id)

        this.isDetailLoading = false
        this.showSuccessNotification(this, 'Contract successfully terminated')
        setTimeout(() => {
          this.cancelForm()
        }, 2000)
      } catch (err) {
        this.isPopUpShown = false
        this.isDetailLoading = false
        this.showFailedNotification(this, 'Cannot terminate contract, please try again')
      }
    },
    async fetchOptionAndDetailData() {
      this.isDetailLoading = true
      await this.fetchUnderCompanies()
      await this.fetchBargeSizes()
      await this.fetchRoutes()
      await this.fetchContractors()
      await this.fetchCurrencies()
      await this.fetchContractDetail()
      await this.getVesselLineUpLog()

      this.isDetailLoading = false
    },
    async goToBargeCertificate(row) {
      await this.$store.dispatch('Contract/setTempContract', ContractTimeForm)

      switch (true) {
        case this.isContractTypeDetail():
          await this.$router.push({
            name: 'ContractManagementDetailBargeCertificate',
            params: { barge: { id: row.id, name: row.barge_name } }
          })
          break
        case this.isContractTypeEdit():
          await this.$router.push({
            name: 'ContractManagementEditBargeCertificate',
            params: { barge: { id: row.id, name: row.barge_name } }
          })
          break
        default:
          await this.$router.push({
            name: 'ContractManagementCreateBargeCertificate',
            params: { barge: { id: row.id, name: row.barge_name } }
          })
          break
      }
    },
    async goToTugCertificate(row) {
      await this.$store.dispatch('Contract/setTempContract', ContractTimeForm)

      switch (true) {
        case this.isContractTypeDetail():
          await this.$router.push({
            name: 'ContractManagementDetailTugCertificate',
            params: { tug: { id: row.id, name: row.tug_name } }
          })
          break
        case this.isContractTypeEdit():
          await this.$router.push({
            name: 'ContractManagementEditTugCertificate',
            params: { tug: { id: row.id, name: row.tug_name } }
          })
          break
        default:
          await this.$router.push({
            name: 'ContractManagementCreateTugCertificate',
            params: { tug: { id: row.id, name: row.tug_name } }
          })
          break
      }
    },
    async putUnitStatus(type, item) {
      try {
        this.isSwitchingUnitStatus = true
        await this.$store.dispatch(type === 'barge'
          ? 'Contract/putBargeActiveState'
          : 'Contract/putTugActiveState',
        item
        )
        this.isSwitchingUnitStatus = false
      } catch (err) {
        this.isSwitchingUnitStatus = false
        this.showFailedNotification(this, 'Cannot update unit status, please try again')
      }
    },
    isContractTypeDetail() {
      return this.$route.name.includes('Detail') || this.isUploading
    },
    isContractTypeEdit() {
      return this.$route.name.includes('Edit')
    },
    setLeaseBase() {
      if (this.charterType === 'TIME') {
        ContractTimeForm.data[0].value = this.startCase(`${this.charterType}_CHARTER`)
        return
      }

      ContractSpotForm.data[0].value = this.startCase(`${this.charterType}_CHARTER`)
    },
    disableDate(item, date) {
      switch (item.key) {
        case 'contract_start': {
          const existingContractStart = this.contractDetail
            ? this.moment(this.contractDetail.contract_start, 'YYYY-MM-DDTHH:mm:ss.SSSZ')
            : null

          if (this.charterType === 'TIME') {
            const contractEnd = this.moment(ContractTimeForm.data[5].value)
            return this.isContractTypeEdit()
              ? date <= existingContractStart
              : date >= contractEnd
          }

          const contractEnd = this.moment(ContractSpotForm.data[5].value)
          return this.isContractTypeEdit()
            ? date <= existingContractStart
            : date >= contractEnd
        }
        case 'contract_end': {
          const existingContractEnd = this.contractDetail
            ? this.moment(this.contractDetail.contract_end, 'YYYY-MM-DDTHH:mm:ss.SSSZ')
            : null

          if (this.charterType === 'TIME') {
            const contractStart = this.moment(ContractTimeForm.data[4].value)
            return this.isContractTypeEdit()
              ? date <= existingContractEnd
              : date <= contractStart
          }

          const contractStart = this.moment(ContractSpotForm.data[4].value)
          return this.isContractTypeEdit()
            ? date <= existingContractEnd
            : date <= contractStart
        }
      }
    },
    generateOptionsByKey(key) {
      switch (key) {
        case 'under_company':
          return this.underCompanyOptions
        case 'barge_size':
          return this.bargeSizeOptions
        case 'contractor':
          return this.contractorOptions
        case 'route':
          return this.routeOptions
        default:
          return []
      }
    },
    showModalByBoatType(type) {
      if (!this.contractor?.value) {
        this.showWarningNotification(this, 'Please select contractor first')
        return
      }

      const isOptionEmpty = this.generateBargeOrTugOptions(type)
      if (!isOptionEmpty.length) {
        this.showWarningNotification(this, `${this.startCase(type)} list is empty`)
        return
      }

      this.isModalShown = true
      this.modal = {
        type,
        header: type === 'barge'
          ? 'Add Barge'
          : 'Add Tug',
        title: type === 'barge'
          ? 'Barge Name'
          : 'Tug Name',
        value: null
      }
    },
    closeModal() {
      this.isModalShown = false
      this.modal = {
        type: null,
        header: null,
        title: null,
        value: null
      }
    },
    upsertBoatByType() {
      if (this.modal.type === 'barge') {
        this.upsertBarge()
        return
      }

      this.upsertTug()
    },
    upsertBarge() {
      if (this.isEditBargeTug) {
        this.bargeData.splice(this.editIndex, 1, { isMoreShown: false, ...this.modal.value.value })
        this.$refs.bargeTable.refresh()
        this.isEditBargeTug = false
        this.editIndex = null
        this.closeModal()
        return
      }

      this.bargeData.push({ isMoreShown: false, ...this.modal.value.value })
      this.bargeUnitStatuses = this.bargeData.map(item => item.unit_status)
      this.closeModal()
    },
    upsertTug() {
      if (this.isEditBargeTug) {
        this.tugData.splice(this.editIndex, 1, { isMoreShown: false, ...this.modal.value.value })
        this.$refs.tugTable.refresh()
        this.isEditBargeTug = false
        this.editIndex = null
        this.closeModal()
        return
      }

      this.tugData.push({ isMoreShown: false, ...this.modal.value.value })
      this.tugUnitStatuses = this.tugData.map(item => item.unit_status)
      this.closeModal()
    },
    handleEditOnMoreMenu(type, item, index) {
      this.editIndex = index
      this.isEditBargeTug = true
      this.isModalShown = true
      this.modal = {
        type,
        header: type === 'barge'
          ? 'Add Barge'
          : 'Add Tug',
        title: type === 'barge'
          ? 'Barge Name'
          : 'Tug Name',
        value: {
          text: type === 'barge'
            ? item.barge_name
            : item.tug_name,
          value: item
        }
      }
    },
    handleDeleteOnMoreMenu(type, index) {
      if (type === 'barge') {
        this.bargeData.splice(index, 1)
        this.bargeUnitStatuses = this.bargeData.map(item => item.unit_status)
        return
      }

      this.tugData.splice(index, 1)
      this.tugUnitStatuses = this.tugData.map(item => item.unit_status)
    },
    generateBargeOrTugOptions(type) {
      if (type === 'barge') {
        return this.bargeOptions
      }

      return this.tugOptions
    },
    transformDataForPost(data, attachments, barges, tugs) {
      let form = {}
      data.forEach(item => {
        switch (item.key) {
          case 'lease': {
            form = { ...form, type: this.charterType }
            break
          }
          case 'contract_start':
          case 'contract_end': {
            form = {
              ...form,
              [item.key]: this.moment(item.value, 'YYYY-MM-DDTHH:mm:ss.SSSZ').format('YYYY-MM-DD')
            }
            break
          }
          case 'rent_price': {
            form = {
              ...form,
              rent_price: item.value.value,
              currency_id: this.currencies.find(currency => this.lowerCase(currency.currency_code) === item.value.currency).id,
              exchange_rate_to_idr: item.value.currency === 'idr'
                ? 1
                : item.value.rate
            }
            break
          }
          case 'contract_number':
          case 'remark': {
            form = { ...form, [item.key]: item.value }
            break
          }
          default: {
            form = { ...form, [item.key]: item.value ? item.value.text : null }
          }
        }
      })

      form = {
        ...form,
        attachment: attachments,
        barge_id: barges.length ? barges.map(item => item.id) : [],
        tug_id: tugs.length ? tugs.map(item => item.id) : []
      }
      return form
    },
    changeButtonState(container) {
      if (this.isProcessing) {
        this.$vs.loading({
          background: 'secondary',
          color: '#FFF',
          container,
          scale: 0.35
        })
        return
      }

      this.$vs.loading.close(container)
    },
    cancelForm() {
      this.resetForm()
      this.$router.push({ name: 'ContractManagementList' })
    },
    resetForm() {
      if (this.charterType === 'TIME') {
        ContractTimeForm.data.forEach(item => {
          item.isDisabled = false
          if (item.key === 'rent_price') {
            item.value = {
              currency: 'idr',
              rate: 0,
              value: null
            }
            return
          }

          item.isDisabled = false
          item.value = null
        })

        ContractTimeForm.attachments = []
        ContractTimeForm.barges = []
        ContractTimeForm.bargeUnitStatuses = []
        ContractTimeForm.tugs = []
        ContractTimeForm.tugUnitStatuses = []
        return
      }

      ContractSpotForm.data.forEach(item => {
        item.isDisabled = false
        if (item.key === 'rent_price') {
          item.value = {
            currency: 'idr',
            rate: 0,
            value: null
          }
          return
        }

        item.isDisabled = false
        item.value = null
      })

      ContractSpotForm.attachments = []
    },
    parseContractFormWhenBackFromCertificate() {
      if (this.isFromCertificate) {
        const tempContract = this.$store.getters['Contract/tempContract']
        if (tempContract) {
          ContractTimeForm.data = tempContract.data.map(item => {
            if (item.key === 'contract_start' || item.key === 'contract_end') {
              item.value = this.moment(item.value).toDate()
            }
            return item
          })
          ContractTimeForm.attachments = tempContract.attachments.map(item => item)
          ContractTimeForm.barges = tempContract.barges.map(item => item)
          ContractTimeForm.bargeUnitStatuses = tempContract.bargeUnitStatuses.map(item => item)
          ContractTimeForm.tugs = tempContract.tugs.map(item => item)
          ContractTimeForm.tugUnitStatuses = tempContract.tugUnitStatuses.map(item => item)
        }
      }
    },
    parseDetailContract(data) {
      if (data.type === 'TIME') {
        this.parseContractTimeDetail(data)
        return
      }

      this.parseContractSpotDetail(data)
    },
    parseContractTimeDetail(data) {
      ContractTimeForm.data.forEach(item => {
        switch (item.key) {
          case 'lease': {
            item.isDisabled = true
            item.value = this.startCase(`${data.type}_CHARTER`)
            break
          }
          case 'under_company': {
            item.isDisabled = true
            item.value = this.underCompanyOptions.find(company => company.text === data.under_company) || null
            break
          }
          case 'barge_size': {
            item.isDisabled = true
            item.value = this.bargeSizeOptions.find(bargeSize => bargeSize.text.includes(data.barge_size)) || null
            break
          }
          case 'contractor': {
            item.isDisabled = true
            item.value = this.contractorOptions.find(contractor => contractor.text === data.contractor) || null
            break
          }
          case 'rent_price': {
            item.isDisabled = !!this.isContractTypeDetail()
            item.value = {
              currency: this.lowerCase(data.currency.currency_code),
              rate: data.currence,
              value: data.rent_price
            }
            break
          }
          default: {
            item.isDisabled = !!this.isContractTypeDetail()
            if (item.key === 'contract_start' || item.key === 'contract_end') {
              item.value = this.moment(data[item.key]).toDate()
              break
            }
            item.value = data[item.key]
            break
          }
        }
      })
      ContractTimeForm.attachments = data.contractAttachments ? data.contractAttachments.map(item => item) : data.attachments.map(item => item)
      ContractTimeForm.barges = data.barge_list.map(item => ({
        id: item.id,
        barge_name: item.name,
        doc_status: item.doc_status,
        unit_status: item.active,
        isMoreShown: false
      }))
      ContractTimeForm.bargeUnitStatuses = ContractTimeForm.barges.length
        ? ContractTimeForm.barges.map(item => item.unit_status)
        : []

      ContractTimeForm.tugs = data.tug_list.map(item => ({
        id: item.id,
        tug_name: item.name,
        doc_status: item.doc_status,
        unit_status: item.active,
        isMoreShown: false
      }))
      ContractTimeForm.tugUnitStatuses = ContractTimeForm.tugs.length
        ? ContractTimeForm.tugs.map(item => item.unit_status)
        : []
    },
    parseContractSpotDetail(data) {
      ContractSpotForm.data.forEach(item => {
        switch (item.key) {
          case 'lease': {
            item.isDisabled = true
            item.value = this.startCase(`${data.type}_CHARTER`)
            break
          }
          case 'barge_size': {
            item.isDisabled = true
            item.value = this.bargeSizeOptions.find(bargeSize => bargeSize.text === data.barge_size) || null
            break
          }
          case 'contractor': {
            item.isDisabled = true
            item.value = this.contractorOptions.find(contractor => contractor.text === data.contractor) || null
            break
          }
          case 'route': {
            item.isDisabled = true
            item.value = this.routeOptions.find(route => route.text === data.route) || null
            break
          }
          case 'rent_price': {
            item.isDisabled = !!this.isContractTypeDetail()
            item.value = {
              currency: this.lowerCase(data.currency.currency_code),
              rate: data.currence,
              value: data.rent_price
            }
            break
          }
          default: {
            item.isDisabled = !!this.isContractTypeDetail()
            if (item.key === 'contract_start' || item.key === 'contract_end') {
              item.value = this.moment(data[item.key]).toDate()
              break
            }
            item.value = data[item.key]
            break
          }
        }
      })
      ContractSpotForm.attachments = data.contractAttachments ? data.contractAttachments.map(item => item) : data.attachments.map(item => item)
    },
    generateFormForEdit(data, attachments, barges, tugs) {
      let editedData = {}
      let form = this.transformDataForPost(data, attachments, barges, tugs)
      const { barge_list, tug_list, ...detail } = this.contractDetail
      const existingData = {
        ...detail,
        barge_id: barge_list.map(barge => barge.id),
        tug_id: tug_list.map(tug => tug.id),
        contract_start: this.moment(this.contractDetail.contract_start, 'YYYY-MM-DDTHH:mm:ss.SSSZ').format('YYYY-MM-DD'),
        contract_end: this.moment(this.contractDetail.contract_end, 'YYYY-MM-DDTHH:mm:ss.SSSZ').format('YYYY-MM-DD'),
        attachment: this.contractDetail.contractAttachments ? this.contractDetail.contractAttachments.map(({ name, dms_node_id, ext, location }) =>
          ({ name, dms_node_id, ext, location })) : this.contractDetail.attachments.map(({ name, dms_node_id, ext, location }) =>
          ({ name, dms_node_id, ext, location }))
      }

      form = {
        ...form,
        attachment: form.attachment.map(({ name, dms_node_id, ext, location }) =>
          ({ name, dms_node_id, ext, location }))
      }

      Object.keys(existingData).forEach(key => {
        switch (true) {
          case key === 'id': {
            editedData = { ...editedData, id: existingData.id }
            break
          }
          case key === 'attachment': {
            if (!this.isEqual(this.loSortBy(form[key]), this.loSortBy(existingData[key]))) {
              editedData = { ...editedData, attachment: form.attachment }
            }
            break
          }
          case key === 'barge_id': {
            if (!this.isEqual(this.loSortBy(form[key]), this.loSortBy(existingData[key]))) {
              editedData = { ...editedData, barge_id: form.barge_id }
            }
            break
          }
          case key === 'tug_id': {
            if (!this.isEqual(this.loSortBy(form[key]), this.loSortBy(existingData[key]))) {
              editedData = { ...editedData, tug_id: form.tug_id }
            }
            break
          }
          case key === 'under_company': {
            editedData = { ...editedData, under_company: form.under_company}
          }
          case form[key] !== undefined: {
            if (form[key] !== existingData[key]) {
              editedData = { ...editedData, [key]: form[key] }
            }
            break
          }
          default:
            break
        }
      })

      return editedData
    },
    handleTerminateLogButtonOnClick() {
      if (this.isContractTypeEdit()) {
        this.isPopUpShown = true
        return
      }
      this.isLogShown = true
    },
    handleTerminateLogButtonVisibility() {
      if (this.isContractTypeEdit()) {
        return this.isUserCanAccessMenu(this.permission.name, 'delete')
      }

      return this.isContractTypeDetail()
    },
    validate() {
      const dataToValidate = this.charterType === 'TIME'
        ? ContractTimeForm
        : ContractSpotForm

      dataToValidate.data.forEach(item => {
        if (!item.isDisabled) {
          if (item.key === 'rent_price') {
            item.isValid = !!item.value.value
            return
          }
          item.isValid = !!item.value
        }
      })

      return dataToValidate.data.some(item => !item.isValid)
    }
  }
}
</script>

<style lang="scss" scoped>
.card-container {
  @apply p-5 my-5 bg-white rounded-lg;
}

.card-table-container {
  @apply p-5 bg-white rounded-lg;
}

/deep/.modal-content-container {
  @apply w-1/4 h-auto overflow-visible #{!important};
}

.select-with-search-bg-white {
  /deep/.bg-accent-multiselect {
    @apply bg-white;
  }
}

.data-table-bg-primary {
  /deep/.vs-table {
    &--td {
      background-color: #F1F1FD !important;
    }
  }
}

.action-button-secondary {
  @apply flex flex-row relative items-center border border-secondary rounded-lg bg-transparent text-secondary cursor-pointer;
}

.doc-status-bg-success {
  @apply px-3 py-1 text-success bg-success-accent font-semibold rounded-2xl;
}

.doc-status-bg-danger {
  @apply px-3 py-1 text-danger bg-danger-accent font-semibold rounded-2xl;
}

.vs-switch-secondary.vs-switch-active {
  @apply bg-secondary-accent;

  /deep/.vs-switch--circle {
    @apply bg-secondary;
  }
}

/deep/.vs-switch--circle {
  @apply bg-grey-001;
}

/deep/.vs-button-danger.vs-button-filled {
  background: rgba(var(--vs-danger), 0.75) !important;
}

.more-container {
  @apply p-5 flex flex-col absolute bg-white rounded-lg shadow-lg;

  left: 125%;
  top: 10%;
  max-height: 20rem;
  min-width: 8rem;
  z-index: 1000;
}

/deep/.modal-content-body {
  span {
    @apply mb-1;
  }

  .vs-textarea {
    @apply bg-white;
    min-height: 6.33rem;
  }
}
</style>
